import { Pie } from 'react-chartjs-2';

import { Chart, plugins, elements, type ChartData } from 'chart.js';
import { buildChartOptions } from 'structure/capital-manager/utils/charts';
import { ChartLegend } from 'structure/capital-manager/components/ChartLedend';
import { TokenTableRow } from 'structure/capital-manager/types';

// Register necessary components
Chart.register(
  elements.ArcElement,
  elements.LineElement,
  elements.PointElement,
  elements.BarElement,
  plugins.Title,
  plugins.Tooltip,
  plugins.Legend,
  plugins.SubTitle
);

const colorMap = {
  none: 'rgb(122,122,122)',
  very_low: 'rgb(94,181,57)',
  low: 'rgb(41,129,184)',
  medium: 'rgb(220,173,73)',
  high: 'rgb(191,75,97)',
};

const labelMap = {
  NONE: 'Lowest',
  VERY_LOW: 'Very low',
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

const getColorForRiskLevel = (riskLevel: string) => {
  return (
    (colorMap as Record<string, string>)[riskLevel.toLowerCase()] ||
    'rgb(191,75,97)'
  ); // default to high risk color if unknown
};

interface RiskLevelChartProps {
  tokens: TokenTableRow[];
}

export const ChartRiskLevel = (props: RiskLevelChartProps) => {
  const { tokens } = props;

  const groupedByRiskLevel = tokens.reduce(
    (acc, token) => {
      const riskLevel = token.riskLevel;

      acc[riskLevel] = Number(token.balanceUsd) + (acc[riskLevel] || 0);

      return acc;
    },
    {} as Record<string, number>
  );

  const sortedEntries = Object.entries(groupedByRiskLevel)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  const labels = sortedEntries.map((item) => item.label);
  const values = sortedEntries.map((item) => item.value);

  const readableLabels = labels.map(
    (label) => (labelMap as Record<string, string>)[label] || label
  );

  const data: ChartData<'pie'> = {
    labels: readableLabels,
    datasets: [
      {
        label: 'Percentage',
        data: values.map((item) => Number(item.toFixed(2))),
        backgroundColor: labels.map((label) => getColorForRiskLevel(label)),
        hoverOffset: 15,
        borderWidth: 0,
      },
    ],
  };

  const options = buildChartOptions({
    title: 'Position risk distribution',
  });

  return (
    <div className='flex flex-col items-center gap-4'>
      <Pie options={options} data={data} />
      <ChartLegend labels={readableLabels} dataset={data.datasets[0]} />
    </div>
  );
};
